import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { FieldInput } from "@/pages/admin/Fields";
import { Switch } from "@/components/ui/switch";

const Card = ({ title, children, testid }) => (
  <section className="card-lux p-6 space-y-5" data-testid={testid}><h2 className="font-serif text-xl font-semibold text-white">{title}</h2>{children}</section>
);
const Row = ({ label, children }) => <div><p className="label">{label}</p>{children}</div>;

const TierEditor = ({ t, onChange }) => {
  const set = (k) => (v) => onChange({ ...t, [k]: v });
  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-4" data-testid={`tier-editor-${t.key}`}>
      <p className="font-accent text-xs text-gold-light uppercase">{t.key}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Row label="Name"><input value={t.name} onChange={(e) => set("name")(e.target.value)} className="field" data-testid={`tier-name-${t.key}`} /></Row>
        <Row label="Tagline"><input value={t.tagline || ""} onChange={(e) => set("tagline")(e.target.value)} className="field" /></Row>
        {t.key !== "free" && <>
          <Row label="Monthly price (USD)"><input type="number" step="0.01" value={t.monthly_price} onChange={(e) => set("monthly_price")(Number(e.target.value))} className="field" data-testid={`tier-monthly-${t.key}`} /></Row>
          <Row label="Yearly price (USD)"><input type="number" step="0.01" value={t.yearly_price} onChange={(e) => set("yearly_price")(Number(e.target.value))} className="field" data-testid={`tier-yearly-${t.key}`} /></Row>
        </>}
      </div>
      <Row label="Benefits (one per line)"><textarea rows={4} value={(t.benefits || []).join("\n")} onChange={(e) => set("benefits")(e.target.value.split("\n"))} className="field" /></Row>
      <div className="flex flex-wrap gap-6 text-sm text-slate-300">
        <label className="flex items-center gap-2"><Switch checked={!!t.featured} onCheckedChange={set("featured")} />Highlight</label>
        {t.key !== "free" && <label className="flex items-center gap-2"><Switch checked={!!t.checkout_enabled} onCheckedChange={set("checkout_enabled")} data-testid={`tier-checkout-${t.key}`} />Accept payments</label>}
      </div>
    </div>
  );
};

const ListEditor = ({ value, onChange, fields, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {value.map((it, i) => (
      <div key={i} className="flex gap-2">
        {fields.map(([k, ph]) => <input key={k} value={it[k] || ""} placeholder={ph} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} className="field !py-2" />)}
        <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400 px-2" aria-label="Remove"><Trash2 size={15} /></button>
      </div>
    ))}
    <button onClick={() => onChange([...value, {}])} className="text-sm text-gold-light flex items-center gap-1"><Plus size={14} />Add</button>
  </div>
);

const Picker = ({ items, value = [], onChange, testid }) => (
  <div className="max-h-56 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-3" data-testid={testid}>
    {items.map((i) => (
      <label key={i.id} className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={value.includes(i.id)} onChange={(e) => onChange(e.target.checked ? [...value, i.id] : value.filter((x) => x !== i.id))} />{i.title}
      </label>
    ))}
  </div>
);

export default function SettingsAdmin({ section }) {
  const { reload } = useSite();
  const [s, setS] = useState(null);
  const [lists, setLists] = useState({ videos: [], courses: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    Promise.all([api.get("/admin/content/videos"), api.get("/admin/content/courses")]).then(([v, c]) => setLists({ videos: v.data, courses: c.data }));
  }, []);
  if (!s) return null;
  const set = (group, k) => (v) => setS((p) => ({ ...p, [group]: { ...p[group], [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const text = (group, k, label, type = "text") => <Row label={label}><FieldInput f={{ k: `${group}-${k}`, type }} value={s[group]?.[k]} onChange={set(group, k)} /></Row>;
  return (
    <div className="space-y-6 max-w-4xl" data-testid={`settings-${section}`}>
      <div className="flex justify-between items-center">
        <h1 className="font-serif text-3xl font-semibold text-white">{section === "pricing" ? "Membership & Pricing" : "Site Content & SEO"}</h1>
        <button disabled={busy} onClick={save} className="btn-gold !py-2" data-testid="settings-save-btn">{busy ? "Saving..." : "Save changes"}</button>
      </div>
      {section === "pricing" ? (
        <Card title="Membership tiers" testid="tiers-card">
          <p className="text-sm text-slate-400">Prices sync to Stripe automatically on save. Existing subscribers keep their current price.</p>
          <label className="flex items-center gap-3 text-sm text-slate-300"><Switch checked={!!s.prices_are_demo} onCheckedChange={(v) => setS({ ...s, prices_are_demo: v })} data-testid="prices-demo-toggle" />Show "demonstration pricing" notice</label>
          {s.tiers.map((t, i) => <TierEditor key={t.key} t={t} onChange={(nt) => setS({ ...s, tiers: s.tiers.map((x, j) => (j === i ? nt : x)) })} />)}
        </Card>
      ) : (
        <>
          <Card title="Homepage" testid="home-settings-card">
            {text("home", "hero_headline", "Hero headline")}
            {text("home", "hero_sub", "Hero supporting text", "textarea")}
            {text("home", "hero_image", "Hero image", "image")}
            {text("home", "intro_eyebrow", "Intro label")}
            {text("home", "intro_title", "Intro title")}
            {text("home", "intro_body", "Intro text", "textarea")}
            {text("home", "cta_title", "Newsletter section title")}
            <Row label="Featured videos (empty = videos marked featured)"><Picker testid="featured-videos-picker" items={lists.videos} value={s.home.featured_video_ids} onChange={set("home", "featured_video_ids")} /></Row>
            <Row label="Featured courses (empty = all)"><Picker testid="featured-courses-picker" items={lists.courses} value={s.home.featured_course_ids} onChange={set("home", "featured_course_ids")} /></Row>
          </Card>
          <Card title="About page">
            {text("about", "title", "Title")}
            {text("about", "tagline", "Tagline")}
            {text("about", "intro", "Introduction (blank line = new paragraph)", "textarea")}
            {text("about", "belief", "Belief statement", "textarea")}
            {text("about", "problem", "The challenge", "textarea")}
            {text("about", "equip_intro", "Journey intro", "textarea")}
            <Row label="Journey pillars"><ListEditor value={s.about.pillars || []} onChange={set("about", "pillars")} fields={[["title", "Title"], ["text", "Description"]]} /></Row>
            {text("about", "lifestyle_title", "Founder section title")}
            {text("about", "lifestyle_body", "Founder section text", "textarea")}
            {text("about", "mission", "Mission", "textarea")}
            {text("about", "closing", "Closing text", "textarea")}
            {text("about", "verse", "Scripture")}
            {text("about", "final_headline", "Final headline")}
            {text("about", "final_body", "Final text", "textarea")}
            {text("about", "image", "Photo", "image")}
          </Card>
          <Card title="Navigation menu"><ListEditor testid="nav-editor" value={s.nav} onChange={(v) => setS({ ...s, nav: v })} fields={[["label", "Label"], ["to", "Link e.g. /videos"]]} /></Card>
          <Card title="SEO defaults">
            {text("seo", "site_url", "Site URL for sitemap (e.g. https://yourdomain.com)")}
            {text("seo", "default_title", "Default title")}
            {text("seo", "default_description", "Default meta description", "textarea")}
            {text("seo", "og_image", "Default sharing image", "image")}
            <p className="text-xs text-slate-500">Sitemap: {process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml</p>
          </Card>
          <Card title="Contact & FAQ">
            {text("contact", "email", "Support email")}
            {text("contact", "support_hours", "Support hours")}
            <ListEditor value={s.faqs} onChange={(v) => setS({ ...s, faqs: v })} fields={[["q", "Question"], ["a", "Answer"]]} />
          </Card>
          <Card title="Privacy & analytics">
            <label className="flex items-center gap-3 text-sm text-slate-300"><Switch checked={s.analytics?.enabled !== false} onCheckedChange={set("analytics", "enabled")} />Enable consent-based first-party analytics</label>
          </Card>
        </>
      )}
    </div>
  );
}
